import _ from 'underscore';

import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';

import { WEBSITE_SOURCE_DATA_KEY_MAPPING } from './site-config-structure';

export const getPageIndexById = (siteConfig, id) => (
  _.chain(siteConfig.pages)
    .findIndex({ id })
    .value()
);

export const getComponentById = (siteConfig, id) => (
  _.chain(siteConfig.pages)
    .pluck('components')
    .flatten()
    .findWhere({ id })
    .value() || {}
);

export const getComponentByTypeFromPageComponents = (components, type) => {
  const index = _.findIndex(components, cmp => cmp.type === type);
  return index >= 0 ?
    _.extend(components[index], { index }) :
    undefined;
};

export const getComponentByType = (siteConfig, type) => (
  _.chain(siteConfig.pages)
    .pluck('components')
    .flatten()
    .findWhere({ type })
    .value()
);

export const getComponentByTypes = (siteConfig, types) => _.map(
  types,
  type => getComponentByType(siteConfig, type)
);

export const getDataSourceByKey = (siteConfig, key, fallback, allowNull = true) => {
  if (key) {
    const actualValue = _.propertyOf(siteConfig.dataSource)(key);
    const fallbackConditions = [
      actualValue === undefined,
      !allowNull && actualValue === null,
    ];

    return _.some(fallbackConditions) ? fallback : actualValue;
  }
  return fallback;
};

export const getDataSourceByKeys = (siteConfig, keys, defaultVals) => _.map(
  keys,
  (key, index) => getDataSourceByKey(siteConfig, key, defaultVals && defaultVals[index])
);

const keyName = {
  siteInfoData: 'dataSource',
  sitePages: 'pages',
  theme: 'theme',
  siteContextInfo: 'siteContextInfo',
  images: 'images',
  orderedComponentsInPages: 'orderedComponentsInPages',
  smartPageDataContext: 'smartPageDataContext',
  websiteSourceData: 'websiteSourceData',
  migrationContext: 'migrationContext',
};

export const getAttributesFromSiteConfig = (siteConfig) => {
  if (!siteConfig) {
    return [];
  }

  let { siteContextInfo } = siteConfig;

  if (siteContextInfo) {
    const {
      protocol,
      host,
      pathname,
    } = window.location;

    siteContextInfo = {
      ...siteContextInfo,
      sourceUrl: `${protocol}//${host}${pathname}`,
    };
  }

  return _.compact([
    siteConfig.dataSource ? {
      Key: keyName.siteInfoData,
      Value: JSON.stringify(siteConfig.dataSource),
    } : null,
    siteConfig.pages ? {
      Key: keyName.sitePages,
      Value: JSON.stringify(siteConfig.pages),
    } : null,
    siteConfig.theme ? {
      Key: keyName.theme,
      Value: JSON.stringify(siteConfig.theme),
    } : null,
    siteConfig.siteContextInfo ? {
      Key: keyName.siteContextInfo,
      Value: JSON.stringify(siteContextInfo),
    } : null,
  ]);
};

// Snapshot is a partial object of siteConfig
export const getAttributesFromSnapshot = (snapshot) => {
  if (!snapshot) {
    return [];
  }

  return _.compact([
    ...getAttributesFromSiteConfig(snapshot),
    snapshot.images ? {
      Key: keyName.images,
      Value: JSON.stringify(snapshot.images),
    } : null,
    snapshot.orderedComponentsInPages ? {
      Key: keyName.orderedComponentsInPages,
      Value: JSON.stringify(snapshot.orderedComponentsInPages),
    } : null,
    snapshot.smartPageDataContext ? {
      Key: keyName.smartPageDataContext,
      Value: JSON.stringify(snapshot.smartPageDataContext),
    } : null,
    snapshot.websiteSourceData ? {
      Key: keyName.websiteSourceData,
      Value: JSON.stringify(snapshot.websiteSourceData),
    } : null,
    snapshot.migrationContext ? {
      Key: keyName.migrationContext,
      Value: JSON.stringify(snapshot.migrationContext),
    } : null,
  ]);
};

export const createSnapshot = siteConfig => cloneDeep(_.pick(siteConfig, _.values(keyName)));

export const getWebsiteSourceDataByKey = (siteConfig = {}, key) => {
  const websiteSourceData = (siteConfig && siteConfig.websiteSourceData) || {};

  return websiteSourceData[key];
};

export const getWebsiteSourceDataByKeys = (siteConfig, keys) => _.map(
  keys,
  key => getWebsiteSourceDataByKey(siteConfig, key)
);

export const isMigratedToWSD = (siteConfig, key) => _.get(siteConfig, ['migrationContext', key], false);

/**
 * Proxy read timezoneId.
 * Timezone Id is stored in ServerContext in main bundle, while in Signup it's stored in the WSD
 * @param {Object} serverContext ServerContext
 * @param {Object} siteConfig Site Config
 * @returns {number} Timezone Id
 */
export const getTimeZoneId = (serverContext, siteConfig) => (
  (serverContext.CurrentAccount && serverContext.CurrentAccount.TimeZone)
  || getWebsiteSourceDataByKey(siteConfig, WEBSITE_SOURCE_DATA_KEY_MAPPING.TimeZoneId)
  || 71
);
